import { zodResolver } from '@hookform/resolvers/zod'
import React from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate } from 'react-router-dom'
import * as z from 'zod'
import { useViewerMaybe } from '~/auth/use-viewer'
import { loginPath, rootPath } from '~/common/paths'
import { TextField } from '~/fields/text-field'
import { Button } from '~/ui/button'
import { Form } from '~/ui/form'
import { useToast } from '~/hooks/use-toast'
import { getMetaContentMaybe } from '~/common/get-meta-content'

const loginFormSchema = z.object({
  email: z.string().email({ message: 'Invalid email address' }),
  password: z.string(),
})

type LoginFormValues = z.infer<typeof loginFormSchema>
type SignInResponse = { redirect_to?: string }

const OAUTH_QUERY_KEYS = [
  'client_id',
  'redirect_uri',
  'state',
  'code_challenge',
  'code_challenge_method',
] as const

const buildOauthParamsFromQuery = () => {
  const allParams = new URLSearchParams(window.location.search)
  const oauthParams = new URLSearchParams()

  for (const key of OAUTH_QUERY_KEYS) {
    const value = allParams.get(key)
    if (value) oauthParams.set(key, value)
  }

  return oauthParams
}

const validatedReturnTo = () => {
  const returnTo = new URLSearchParams(window.location.search).get('returnTo')
  if (!returnTo) return null

  if (returnTo.startsWith('/') && !returnTo.startsWith('//')) {
    return returnTo
  }

  if (returnTo.startsWith('spin-command://')) {
    return returnTo
  }

  if (/^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/i.test(returnTo)) {
    return returnTo
  }

  return null
}

export const CredentialsLoginScreen: React.FC = () => {
  const { viewer } = useViewerMaybe()
  const { toast } = useToast()
  const navigate = useNavigate()
  const {
    result: { refetch: refetchViewer },
  } = useViewerMaybe()

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const onSubmit = async (values: LoginFormValues) => {
    const oauthParams = buildOauthParamsFromQuery()
    const hasOauthFlow = oauthParams.size > 0
    const signInPath = hasOauthFlow ? `/users/sign_in?${oauthParams.toString()}` : '/users/sign_in'
    const csrfToken = getMetaContentMaybe('csrf-token')
    const body = new URLSearchParams({
      'user[email]': values.email,
      'user[password]': values.password,
      'user[remember_me]': '1',
    }).toString()

    let response: Response
    try {
      response = await fetch(signInPath, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          'X-Requested-With': 'XMLHttpRequest',
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
          Accept: 'application/json,text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
        },
        credentials: 'same-origin',
        body,
      })
    } catch {
      toast({
        title: 'Login Failed',
        description: 'Unable to reach the server. Please try again.',
        variant: 'destructive',
      })
      return
    }

    if (!response.ok) {
      toast({
        title: 'Login Failed',
        description: 'Invalid email or password.',
        variant: 'destructive',
      })
      return
    }

    const contentType = response.headers.get('content-type') ?? ''
    if (contentType.includes('application/json')) {
      const signInResponse = (await response.json()) as SignInResponse
      if (signInResponse.redirect_to) {
        window.location.assign(signInResponse.redirect_to)
        return
      }
    }

    if (hasOauthFlow) {
      window.location.assign(`/oauth/authorize?${oauthParams.toString()}`)
      return
    }

    await refetchViewer()
    toast({
      title: 'Login Successful',
      description: 'You have been successfully logged in.',
      variant: 'default',
    })

    const returnTo = validatedReturnTo()
    if (returnTo) {
      if (returnTo.startsWith('/')) {
        navigate(returnTo)
      } else {
        window.location.assign(returnTo)
      }
      return
    }

    navigate(rootPath({}))
  }

  return (
    <div className="bg-background flex min-h-screen flex-col items-center justify-center">
      <div className="w-full max-w-md space-y-8">
        <h1 className="text-center text-2xl font-bold">Login with Credentials</h1>
        {viewer ? (
          <p className="text-center">You are already logged in.</p>
        ) : (
          <>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <TextField
                  control={form.control}
                  name="email"
                  label="Email"
                  placeholder="Enter your email"
                />
                <TextField
                  control={form.control}
                  name="password"
                  label="Password"
                  type="password"
                  placeholder="Enter your password"
                />
                <Button type="submit" className="w-full">
                  Login
                </Button>
              </form>
            </Form>
            <div className="text-center">
              <Link to={loginPath({})} className="text-sm text-blue-600 hover:underline">
                Login with email link
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
